import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Plus, Loader2, Search, User, Mail, Phone, Calendar, Trash2, Gift, Pencil, KeyRound, Copy, Check, Upload, Download, FileText } from "lucide-react";
import IconAction from "@/components/IconAction";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";
import { useAuth } from "@/contexts/AuthContext";
import { useTerm } from "@/contexts/branding";

const TID = {
  newBtn: "clients-new-btn",
  search: "clients-search",
  row: (id) => `client-row-${id}`,
  view: (id) => `client-view-${id}`,
  remove: (id) => `client-remove-${id}`,
  formName: "client-form-name",
  formEmail: "client-form-email",
  formPhone: "client-form-phone",
  formBirthday: "client-form-birthday",
  formSubmit: "client-form-submit",
};

export default function TenantClients() {
  const [items, setItems] = useState([]);
  const [riskMap, setRiskMap] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const auth = useAuth();
  const term = useTerm(auth?.tenant);
  const { slug } = useParams();
  const customFields = (auth?.tenant?.custom_fields || []).filter((f) => f.resource === "client");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: risk }] = await Promise.all([
        api.get("/tenant/clients", { params: { q: search || undefined } }),
        api.get("/tenant/clients/at-risk?bucket=all").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(data.items || []);
      const m = {};
      (risk.items || []).forEach((r) => { m[r.client_id] = r.risk_level; });
      setRiskMap(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/tenant/clients/${id}/timeline`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Excluir este ${term("client_singular", { lower: true })}?`)) return;
    try { await api.delete(`/tenant/clients/${id}`); toast.success(`${term("client_singular")} removido`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{term("client_plural")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 break-words">Base de {term("client_plural", { lower: true })}</h1>
          <p className="text-sm text-muted-foreground mt-1">Cadastre, segmente e acompanhe seus {term("client_plural", { lower: true })}.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ImportClientsButton onImported={load} term={term} />
          <CreateClientButton open={openCreate} setOpen={setOpenCreate} onCreated={load} customFields={customFields} term={term} />
        </div>
      </div>

      <div className="mb-4 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="pl-9" placeholder="Buscar por nome, email ou telefone"
          data-testid={TID.search} />
      </div>

      <Card className="rounded-tenant border-border overflow-hidden">
        {loading ? <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline-block" /></div> :
          items.length === 0 ? <div className="p-12 text-center text-muted-foreground">Nenhum {term("client_singular", { lower: true })} ainda. Cadastre o primeiro.</div> :
          (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">{term("client_singular")}</th>
                  <th className="text-left px-5 py-3 font-medium">Contato</th>
                  <th className="text-right px-5 py-3 font-medium">Pontos</th>
                  <th className="text-left px-5 py-3 font-medium">Tags</th>
                  <th className="text-right px-5 py-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid={TID.row(c.id)} className="border-t border-border hover:bg-muted/30">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">desde {new Date(c.created_at).toLocaleDateString("pt-BR")}</div>
                        </div>
                        {riskMap[c.id] && riskMap[c.id] !== "on_track" && (
                          <Badge
                            variant="outline"
                            data-testid={`client-risk-${c.id}`}
                            className={
                              riskMap[c.id] === "warning_30" ? "bg-amber-500/10 text-amber-700 border-amber-500/30" :
                              riskMap[c.id] === "critical_60" ? "bg-orange-500/10 text-orange-700 border-orange-500/30" :
                              "bg-red-500/10 text-red-700 border-red-500/30"
                            }
                          >
                            {riskMap[c.id] === "warning_30" && "30d sem voltar"}
                            {riskMap[c.id] === "critical_60" && "60d sem voltar"}
                            {riskMap[c.id] === "lost_90" && "90d+ sem voltar"}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-muted-foreground">
                      {c.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone}</div>}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <span className="inline-flex items-center gap-1 font-mono font-semibold">
                        <Gift className="w-3.5 h-3.5 text-tenant" /> {c.points_balance || 0}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      {(c.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="outline" className="mr-1">{t}</Badge>)}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <PortalAccessButton client={c} onChanged={load} />
                        <EditClientButton client={c} customFields={customFields} onSaved={load} term={term} />
                        <IconAction icon={User} label="Ver detalhes do cliente" testid={TID.view(c.id)} onClick={() => openDetail(c.id)} />
                        <IconAction icon={Trash2} label="Excluir cliente" testid={TID.remove(c.id)} className="text-destructive hover:text-destructive" onClick={() => remove(c.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
      </Card>

      <ClientDetailSheet detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function CreateClientButton({ open, setOpen, onCreated, customFields, term }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cpf: "", birthday: "", notes: "", custom: {} });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.birthday) delete payload.birthday;
      if (payload.cpf) payload.cpf = payload.cpf.replace(/\D/g, "");
      if (!payload.cpf) delete payload.cpf;
      try {
        await api.post("/tenant/clients", payload);
      } catch (err) {
        // Item 8 — Duplicata por telefone/CPF: back retorna 409 duplicate_client.
        const detail = err?.response?.data?.detail;
        if (detail?.code === "duplicate_client") {
          const names = (detail.duplicates || []).map((d) => d.name).filter(Boolean);
          const byLabel = (detail.matched_by || []).join(" e ") || "telefone/CPF";
          const msg =
            `Já existe ${term("client_singular", { lower: true })} com o mesmo ${byLabel}` +
            (names.length ? ` (${names.join(", ")})` : "") +
            `. Cadastrar mesmo assim?`;
          const ok = window.confirm(msg);
          if (!ok) {
            setBusy(false);
            return;
          }
          await api.post("/tenant/clients?allow_duplicate=true", payload);
        } else {
          throw err;
        }
      }
      toast.success(`${term("client_singular")} cadastrado`);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", cpf: "", birthday: "", notes: "", custom: {} });
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={TID.newBtn} className="gap-2"
          style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
          <Plus className="w-4 h-4" /> Novo {term("client_singular", { lower: true })}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Novo {term("client_singular", { lower: true })}</DialogTitle>
          <DialogDescription>Cadastre alguém na sua base. Campos personalizados aparecem aqui.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input required data-testid={TID.formName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" data-testid={TID.formEmail} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Telefone</Label>
              <Input data-testid={TID.formPhone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>CPF <span className="text-muted-foreground text-xs">(portal)</span></Label>
              <Input
                data-testid="client-form-cpf"
                inputMode="numeric"
                placeholder="000.000.000-00"
                value={form.cpf}
                onChange={(e) => {
                  const d = e.target.value.replace(/\D/g, "").slice(0, 11);
                  const masked = d.length <= 3 ? d :
                    d.length <= 6 ? `${d.slice(0,3)}.${d.slice(3)}` :
                    d.length <= 9 ? `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}` :
                    `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
                  setForm({ ...form, cpf: masked });
                }}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Aniversário</Label>
            <Input type="date" data-testid={TID.formBirthday} value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Notas</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          {customFields.length > 0 && (
            <div className="pt-2 border-t border-border space-y-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Campos personalizados</div>
              {customFields.map((f) => (
                <div key={f.id} className="space-y-1.5">
                  <Label>{f.label}{f.required && " *"}</Label>
                  <Input type={f.field_type === "number" ? "number" : f.field_type === "date" ? "date" : "text"}
                    value={form.custom[f.key] || ""}
                    onChange={(e) => setForm({ ...form, custom: { ...form.custom, [f.key]: e.target.value } })} />
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button type="submit" data-testid={TID.formSubmit} disabled={busy} className="gap-2"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Cadastrar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ClientDetailSheet({ detail, onClose }) {
  const iconFor = (kind) => {
    if (kind === "appointment") return <Calendar className="w-4 h-4 text-blue-600" />;
    if (kind === "loyalty") return <Gift className="w-4 h-4 text-tenant" />;
    if (kind === "redemption") return <User className="w-4 h-4 text-amber-600" />;
    if (kind === "referral") return <Mail className="w-4 h-4 text-emerald-600" />;
    return null;
  };
  return (
    <Sheet open={!!detail} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {detail && (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-2xl">{detail.client.name}</SheetTitle>
              <SheetDescription>
                {detail.client.email || "—"} · {detail.client.phone || "—"}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-4 gap-3">
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pontos</div>
                  <div className="font-display text-2xl font-bold text-tenant">{detail.client.points_balance || 0}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Visitas</div>
                  <div className="font-display text-2xl font-bold">{detail.summary?.appointments || 0}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Resgates</div>
                  <div className="font-display text-2xl font-bold">{detail.summary?.redemptions || 0}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Indicações</div>
                  <div className="font-display text-2xl font-bold">{detail.summary?.referrals || 0}</div>
                </Card>
              </div>

              <RecurrenceCard recurrence={detail.recurrence} client={detail.client} />

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Linha do tempo</div>
                {(detail.events || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">Sem atividade ainda.</div>
                ) : (
                  <ul className="relative border-l border-border pl-5 space-y-4">
                    {detail.events.map((e, i) => (
                      <li key={i} className="relative">
                        <div className="absolute -left-[26px] top-0 w-4 h-4 rounded-full bg-card border border-border grid place-items-center">
                          {iconFor(e.kind)}
                        </div>
                        <div className="text-sm font-medium">{e.title}</div>
                        <div className="text-xs text-muted-foreground">{e.subtitle}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{new Date(e.at).toLocaleString("pt-BR")}</div>
                        {e.kind === "appointment" && (e.data?.products_used?.length || e.data?.photos?.length) ? (
                          <div className="mt-2 ml-0 space-y-1.5">
                            {e.data.products_used?.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">Produtos:</span> {e.data.products_used.join(", ")}
                              </div>
                            )}
                            {e.data.photos?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {e.data.photos.slice(0, 4).map((url, j) => (
                                  <img key={j} src={url} alt={`Atendimento ${j+1}`} className="w-14 h-14 rounded-md object-cover border border-border" />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------- Sprint 1: Card de Recorrência (próximo retorno + risco) ----------
const RISK_META = {
  on_track:   { label: "Em dia",            tone: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
  warning_30: { label: "Atrasado 30 dias",  tone: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  critical_60:{ label: "Crítico 60 dias",   tone: "bg-orange-500/10 text-orange-700 border-orange-500/30" },
  lost_90:    { label: "Cliente perdido",   tone: "bg-red-500/10 text-red-700 border-red-500/30" },
  unknown:    { label: "Sem histórico",     tone: "bg-muted text-muted-foreground border-border" },
};

function RecurrenceCard({ recurrence, client }) {
  if (!recurrence) return null;
  const meta = RISK_META[recurrence.risk_level] || RISK_META.unknown;
  const nextAt = recurrence.next_return_at ? new Date(recurrence.next_return_at) : null;
  const canSend = !!client?.phone && recurrence.risk_level && recurrence.risk_level !== "on_track" && recurrence.risk_level !== "unknown";
  const message = `Olá ${client?.name || ""}! Está na hora do seu retorno de ${recurrence.last_service_name || "atendimento"}. Quando podemos agendar?`;
  return (
    <Card className="p-4 rounded-tenant border-border" data-testid="recurrence-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recorrência</div>
          <div className="font-display font-semibold text-base mt-0.5">
            {recurrence.last_service_name ? `Último: ${recurrence.last_service_name}` : "Sem atendimento concluído"}
          </div>
        </div>
        <Badge variant="outline" className={meta.tone}>{meta.label}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Dias desde o último</div>
          <div className="font-medium mt-0.5">{recurrence.days_since_last ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Próximo retorno sugerido</div>
          <div className="font-medium mt-0.5">
            {nextAt ? nextAt.toLocaleDateString("pt-BR") : "—"}
          </div>
        </div>
      </div>
      {canSend && (
        <button
          type="button"
          onClick={() => sendWhatsApp({ phone: client.phone, text: message, clientId: client.id, context: "recurrence_card" })}
          data-testid="recurrence-wa-link"
          className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors"
        >
          📱 Chamar de volta no WhatsApp
        </button>
      )}
    </Card>
  );
}


// ---------- Sprint C: Portal do Cliente (login por CPF) ----------
function PortalAccessButton({ client, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enabledResult, setEnabledResult] = useState(null); // { cpf, login_url }
  const [copied, setCopied] = useState(false);
  const enabled = !!client.portal_enabled;
  const hasCpf = !!(client.cpf && String(client.cpf).replace(/\D/g, "").length === 11);

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenant/clients/${client.id}/portal/enable`);
      setEnabledResult(data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Revogar acesso ao portal deste cliente?")) return;
    setBusy(true);
    try {
      await api.post(`/tenant/clients/${client.id}/portal/disable`);
      toast.success("Acesso revogado.");
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Não foi possível copiar.");
    }
  };

  const fmtCpf = (raw) => {
    const d = String(raw || "").replace(/\D/g, "");
    if (d.length !== 11) return raw;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
  };

  return (
    <>
      <IconAction
        icon={KeyRound}
        label={enabled ? "Portal do cliente: ativo" : "Liberar acesso ao portal"}
        testid={`client-portal-${client.id}`}
        className={enabled ? "text-emerald-700 hover:text-emerald-800" : ""}
        onClick={() => setOpen(true)}
      />
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setEnabledResult(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Portal do cliente</DialogTitle>
            <DialogDescription>
              {client.name} {client.cpf ? `· CPF ${fmtCpf(client.cpf)}` : ""}
            </DialogDescription>
          </DialogHeader>

          {enabledResult ? (
            <div className="space-y-3" data-testid="portal-creds">
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 text-sm">
                Acesso liberado! Compartilhe o CPF com o cliente — ele entra direto, sem senha.
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">CPF (credencial)</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={fmtCpf(enabledResult.cpf)} className="font-mono text-sm" data-testid="portal-cpf" />
                  <Button type="button" size="icon" variant="outline" onClick={() => copyText(fmtCpf(enabledResult.cpf))}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                URL do portal: <span className="font-mono">{enabledResult.login_url}</span>
              </div>
            </div>
          ) : !enabled ? (
            <div className="space-y-3">
              {!hasCpf ? (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-sm text-amber-700">
                  Cadastre o <strong>CPF</strong> deste cliente antes de liberar o acesso.
                  O CPF é a credencial única do portal (sem senha).
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Ao liberar, o cliente acessa o portal usando apenas o CPF. Ele poderá
                  ver agendamentos, confirmar presença, responder pesquisa NPS, ver pontos
                  e solicitar resgate de prêmios.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                Acesso ao portal está <strong>ativo</strong>. Login do cliente: CPF.
              </div>
              <p className="text-muted-foreground">
                Você pode revogar o acesso a qualquer momento.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {!enabled && !enabledResult && (
              <Button onClick={enable} disabled={busy || !hasCpf} data-testid="portal-enable-btn"
                style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Liberar acesso
              </Button>
            )}
            {enabled && !enabledResult && (
              <Button variant="destructive" onClick={disable} disabled={busy} data-testid="portal-disable-btn">
                Revogar acesso
              </Button>
            )}
            {enabledResult && (
              <Button variant="outline" onClick={() => { setOpen(false); setEnabledResult(null); }}>
                Fechar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ---------- Editar cliente ----------

function EditClientButton({ client, customFields, onSaved, term }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    // Inicializa o form com os dados atuais quando abre
    const rawCpf = client.cpf ? client.cpf.replace(/\D/g, "") : "";
    const cpfMasked = rawCpf.length === 11
      ? `${rawCpf.slice(0,3)}.${rawCpf.slice(3,6)}.${rawCpf.slice(6,9)}-${rawCpf.slice(9,11)}`
      : rawCpf;
    setForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      cpf: cpfMasked,
      birthday: client.birthdate || client.birthday || "",
      notes: client.notes || "",
      tags: (client.tags || []).join(", "),
      custom: { ...(client.custom_fields || client.custom || {}) },
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      if (payload.cpf) payload.cpf = payload.cpf.replace(/\D/g, "");
      if (!payload.cpf) delete payload.cpf;
      if (!payload.email) delete payload.email;
      if (!payload.birthday) delete payload.birthday;
      await api.put(`/tenant/clients/${client.id}`, payload);
      toast.success(`${term("client_singular")} atualizado`);
      setOpen(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <IconAction
        icon={Pencil}
        label="Editar dados do cliente"
        testid={`client-edit-${client.id}`}
        onClick={openDialog}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="client-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Editar {term("client_singular", { lower: true })}</DialogTitle>
            <DialogDescription>Atualize os dados de {client.name}.</DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input required data-testid="client-edit-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" data-testid="client-edit-email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefone</Label>
                  <Input data-testid="client-edit-phone" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>CPF <span className="text-muted-foreground text-xs">(portal)</span></Label>
                  <Input
                    data-testid="client-edit-cpf"
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    value={form.cpf}
                    onChange={(e) => {
                      const d = e.target.value.replace(/\D/g, "").slice(0, 11);
                      const masked = d.length <= 3 ? d :
                        d.length <= 6 ? `${d.slice(0,3)}.${d.slice(3)}` :
                        d.length <= 9 ? `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}` :
                        `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
                      setForm({ ...form, cpf: masked });
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Aniversário</Label>
                  <Input type="date" data-testid="client-edit-birthday" value={form.birthday}
                    onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Tags <span className="text-muted-foreground text-xs">(separadas por vírgula)</span></Label>
                  <Input data-testid="client-edit-tags" placeholder="vip, mensalista, aniversariante" value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Notas</Label>
                <Textarea rows={3} data-testid="client-edit-notes" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>

              {customFields.length > 0 && (
                <div className="pt-2 border-t border-border space-y-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Campos personalizados</div>
                  {customFields.map((cf) => (
                    <div key={cf.key} className="space-y-1.5">
                      <Label>{cf.label}</Label>
                      <Input
                        data-testid={`client-edit-cf-${cf.key}`}
                        value={form.custom?.[cf.key] || ""}
                        onChange={(e) => setForm({ ...form, custom: { ...form.custom, [cf.key]: e.target.value } })}
                      />
                    </div>
                  ))}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  disabled={busy}
                  className="gap-2"
                  data-testid="client-edit-submit"
                  style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Salvar alterações
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


// ---------- Importação em lote de clientes (CSV) ----------

function ImportClientsButton({ onImported, term }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/tenant/clients/import/template", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modelo-importacao-clientes.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Modelo baixado! Preencha e faça upload de volta.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tenant/clients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} ${term("client_plural", { lower: true })} importados!`);
        onImported?.();
      } else {
        toast.warning("Nenhum cliente foi importado. Confira os detalhes.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="clients-import-btn">
          <Upload className="w-4 h-4" /> Importar CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="clients-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Importar {term("client_plural", { lower: true })} em lote</DialogTitle>
          <DialogDescription>
            Migre sua base de outra ferramenta ou de uma planilha. Aceita arquivo CSV (UTF-8) com até 5MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="p-4 border-border rounded-md bg-muted/30">
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="text-xs space-y-1.5 flex-1">
                <div className="font-medium text-sm">Colunas aceitas</div>
                <div className="text-muted-foreground">
                  <code className="bg-background px-1 rounded">nome</code> (obrigatório) ·{" "}
                  <code className="bg-background px-1 rounded">telefone</code> ·{" "}
                  <code className="bg-background px-1 rounded">email</code> ·{" "}
                  <code className="bg-background px-1 rounded">cpf</code> ·{" "}
                  <code className="bg-background px-1 rounded">aniversario</code>
                </div>
                <div className="text-muted-foreground">
                  Aniversário aceita <code className="bg-background px-1 rounded">YYYY-MM-DD</code> ou{" "}
                  <code className="bg-background px-1 rounded">DD/MM/YYYY</code>. Duplicados (telefone/email já cadastrado) são ignorados automaticamente.
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={downloadTemplate} className="gap-1.5 shrink-0" data-testid="clients-import-template-btn">
                <Download className="w-3.5 h-3.5" /> Baixar modelo
              </Button>
            </div>
          </Card>

          {!result && (
            <div className="space-y-2">
              <Label>Arquivo CSV</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="clients-import-file"
              />
              {file && <div className="text-xs text-muted-foreground">Selecionado: {file.name} ({Math.round(file.size / 1024)} KB)</div>}
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="clients-import-result">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{result.created}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Criados</div>
                </div>
                <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-center">
                  <div className="text-2xl font-bold text-amber-700">{result.skipped}</div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700">Ignorados</div>
                </div>
                <div className="p-3 rounded-md border border-red-500/30 bg-red-500/10 text-center">
                  <div className="text-2xl font-bold text-red-700">{result.errors}</div>
                  <div className="text-[10px] uppercase tracking-wider text-red-700">Erros</div>
                </div>
              </div>

              {result.details.skipped.length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer text-muted-foreground">Ver duplicados ignorados ({result.details.skipped.length})</summary>
                  <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto pl-4">
                    {result.details.skipped.map((s, i) => (
                      <li key={i}>• linha {s.row}: <strong>{s.name}</strong> — {s.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              {result.details.errors.length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer text-red-700">Ver erros ({result.details.errors.length})</summary>
                  <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto pl-4">
                    {result.details.errors.map((s, i) => (
                      <li key={i}>• linha {s.row}: {s.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" onClick={reset} data-testid="clients-import-again">Importar outro</Button>
              <Button onClick={() => setOpen(false)} data-testid="clients-import-done">Fechar</Button>
            </>
          ) : (
            <Button
              onClick={doImport}
              disabled={!file || busy}
              className="gap-2"
              data-testid="clients-import-submit"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Importar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
